/**
 * Qué se pide y qué se dibuja de las opciones de una oferta, decidido por los
 * DATOS y nunca por el vertical.
 *
 * El hub publica `offers/options` para los nueve verticales y quién tiene algo
 * que elegir lo decide el CONECTOR, no la familia de producto: los tipos de
 * tarifa de un circuito de tui-multidaytours (habitación × categoría de hotel)
 * llegan por el mismo endpoint que las acomodaciones de una travesía. Por eso
 * el pedido no recibe el vertical y el montaje sale de la FORMA de la
 * respuesta: un conector nuevo con opciones se enciende solo, sin agregar una
 * línea acá.
 */

import type { AccommodationOption, OfferOptions } from './queries';

/** El pedido de opciones sobre una oferta, o `null` si no hay oferta que abrir. */
export function offerOptionsRequestFor(
  quote: { offer: { provenance: string } } | null | undefined,
): { provenanceToken: string } | null {
  return quote ? { provenanceToken: quote.offer.provenance } : null;
}

/**
 * Acomodación con geometría: sin fila Y columna no se puede posicionar en la
 * grilla. Es la MISMA expresión con la que el mapa filtra lo que dibuja, así
 * no puede montarse un mapa que después se auto-oculta por no tener nada.
 */
export function hasSeatGeometry(option: AccommodationOption): boolean {
  return typeof option.row === 'number' && typeof option.column === 'number';
}

/**
 * Con qué forma se eligen las opciones publicadas:
 *
 * - `seatMap`: hay geometría de asiento — se dibuja la unidad (butacas por
 *   fila/columna/piso) y la calidad se cambia mirándola.
 * - `optionsList`: hay algo que elegir sin geometría — plazas por tramo (con
 *   su cantidad) y/o el radio de tipos de tarifa.
 */
export type OfferOptionsPicker = 'seatMap' | 'optionsList';

export interface OfferOptionsRender {
  /** `null` = no hay nada que elegir: no se dibuja ninguna sección. */
  picker: OfferOptionsPicker | null;
  /**
   * El texto del operador cuando NO hay picker que ya lo muestre. Es contenido
   * suyo (restricciones de la travesía, avisos del producto), así que no se
   * pierde por no haber nada que elegir; sin texto no se inventa ningún aviso.
   */
  standaloneMessage: string | null;
  /**
   * `unavailable` = el pedido falló de verdad y hay que decirlo. El 400 NO
   * llega acá: es la respuesta documentada del conector sin opciones por
   * oferta, o sea el caso normal de la mayoría de los verticales.
   */
  notice: 'unavailable' | null;
}

const NOTHING: OfferOptionsRender = {
  picker: null,
  standaloneMessage: null,
  notice: null,
};

/**
 * El fallo del pedido de opciones, cuando lo hubo. `status` es el HTTP del
 * backend; ausente cuando no hubo respuesta (timeout, red caída).
 */
export interface OfferOptionsFailure {
  status?: number;
}

/**
 * Un 400 es lo que el contrato del hub define para un conector SIN opciones por
 * oferta: es el caso normal —el de los verticales que hoy no piden nada— y no
 * se avisa. Cualquier otro fallo sí: quedarse sin el mapa de asientos en
 * silencio deja al asesor sin saber por qué no puede elegir (21.4).
 */
export function offerOptionsFailureIsNotable(
  failure: OfferOptionsFailure,
): boolean {
  return failure.status !== 400;
}

/**
 * Qué monta el modal con las opciones que volvieron.
 *
 * Sin opciones no se dibuja NADA: mientras cargan (el CTA ya dice por qué está
 * deshabilitado, al lado del botón) y con el 400 del conector sin la capacidad,
 * que es el caso normal. Un fallo REAL sí se avisa (`notice`).
 *
 * La geometría manda sobre la lista porque el mapa es el control específico de
 * ese dato; si el proveedor mezclara asientos con y sin coordenadas, se dibuja
 * la unidad con los que se pueden posicionar (el mismo comportamiento que hoy
 * tienen los micros — unificar los dos pickers es otro trabajo).
 */
export function resolveOfferOptionsRender({
  options,
  failure,
}: {
  options: OfferOptions | undefined;
  /** El pedido falló; ausente = no falló (todavía cargando, o respondió). */
  failure?: OfferOptionsFailure;
}): OfferOptionsRender {
  if (!options) {
    return failure && offerOptionsFailureIsNotable(failure)
      ? { ...NOTHING, notice: 'unavailable' }
      : NOTHING;
  }

  const picker: OfferOptionsPicker | null = options.accommodations.some(
    hasSeatGeometry,
  )
    ? 'seatMap'
    : options.accommodations.length > 0 || options.ticketTypes.length > 0
      ? 'optionsList'
      : null;

  return {
    picker,
    standaloneMessage:
      picker === null ? options.message?.trim() || null : null,
    notice: null,
  };
}

/**
 * Si la selección hecha sobre un MAPA de asientos se puede reservar.
 *
 * Elegir asiento es opcional y de todo-o-nada: el hub documenta "sin elección
 * se auto-asignan los primeros libres de la calidad; con elección, una por
 * pasajero", y no hay evidencia de que el operador rechace una parcial. La
 * regla es del mapa —de cualquier conector que publique geometría—, no del
 * vertical, y por eso recibe el MISMO `picker` que decide dibujarlo: no puede
 * exigirse una forma de elegir que la pantalla no está ofreciendo.
 *
 * En la lista de plazas no hay regla nuestra: si la selección vale la decide
 * el operador al cotizarla.
 */
export function seatSelectionValid({
  picker,
  selectedCount,
  passengers,
}: {
  picker: OfferOptionsPicker | null;
  selectedCount: number;
  passengers: number;
}): boolean {
  if (picker !== 'seatMap') return true;
  return selectedCount === 0 || selectedCount === passengers;
}
